import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { RouteComponentProps } from '@reach/router'
import UserContext, { Roles } from '../../UserContext'
import PageHeading from '../Common/PageHeading'
import { ApiFetch, ApiPost } from '../Common/Api'
import type {
    GuidingCareReplicaActivityResponse,
    GuidingCareReplicaActivitySource,
    GuidingCareReplicaEnrichResponse,
    GuidingCareReplicaReprocessResponse,
    GuidingCareReplicaResetPollDateRequest,
    GuidingCareReplicaResetPollDateResponse
} from '../../models/GuidingCareReplica'

type StatusType = 'idle' | 'saving' | 'success' | 'error'
type ReportLoadOptions = {
    source?: GuidingCareReplicaActivitySource
    page?: number
    pageSize?: number
}

const toDateInputValue = (date: Date): string => {
    const pad = (value: number) => value.toString().padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

const displayLocalDateTime = (value?: string): string => {
    if (!value) return 'N/A'
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return value
    return parsed.toLocaleString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZoneName: 'short'
    })
}

const getLevelBadgeStyle = (level?: string) => {
    const normalized = (level || '').toLowerCase()
    if (normalized === 'error') {
        return {
            backgroundColor: '#fde8e8',
            color: '#9b1c1c'
        }
    }

    if (normalized === 'warning') {
        return {
            backgroundColor: '#fff8e1',
            color: '#8d6e00'
        }
    }

    return {
        backgroundColor: '#e8f3ff',
        color: '#0b4a7f'
    }
}

const buildDirectPageNumbers = (currentPage: number, totalPages: number, maxButtons = 5): number[] => {
    if (totalPages <= maxButtons)
        return Array.from({ length: totalPages }, (_, idx) => idx + 1)

    const side = Math.floor(maxButtons / 2)
    let start = Math.max(1, currentPage - side)
    let end = Math.min(totalPages, start + maxButtons - 1)

    if ((end - start + 1) < maxButtons) {
        start = Math.max(1, end - maxButtons + 1)
    }

    return Array.from({ length: end - start + 1 }, (_, idx) => start + idx)
}

const GuidingCareReplicaAdmin: React.FC<RouteComponentProps> = () => {
    const { hasAnyRole } = useContext(UserContext)

    const now = new Date()
    const defaultFrom = new Date(now.getTime() - (24 * 60 * 60 * 1000))
    const [reportFromLocal, setReportFromLocal] = useState<string>(toDateInputValue(defaultFrom))
    const [reportToLocal, setReportToLocal] = useState<string>(toDateInputValue(now))
    const [reportSource, setReportSource] = useState<GuidingCareReplicaActivitySource>('Replica')
    const [reportPage, setReportPage] = useState<number>(1)
    const [reportPageSize, setReportPageSize] = useState<number>(25)
    const [loadingReport, setLoadingReport] = useState<boolean>(false)
    const [reportErrorText, setReportErrorText] = useState<string>('')
    const [activityData, setActivityData] = useState<GuidingCareReplicaActivityResponse | null>(null)
    const activityHeadingRef = useRef<HTMLHeadingElement | null>(null)
    const lastFocusedPageRef = useRef<number>(1)

    const [lastPollDateLocal, setLastPollDateLocal] = useState<string>('')
    const [shiftHours, setShiftHours] = useState<number>(0)
    const [resetStatus, setResetStatus] = useState<StatusType>('idle')
    const [resetErrorText, setResetErrorText] = useState<string>('')
    const [result, setResult] = useState<GuidingCareReplicaResetPollDateResponse | null>(null)

    const [reprocessLookbackHours, setReprocessLookbackHours] = useState<number>(24)
    const [reprocessShiftHours, setReprocessShiftHours] = useState<number>(0)
    const [reprocessStatus, setReprocessStatus] = useState<StatusType>('idle')
    const [reprocessErrorText, setReprocessErrorText] = useState<string>('')
    const [reprocessResult, setReprocessResult] = useState<GuidingCareReplicaReprocessResponse | null>(null)

    const [enrichStatus, setEnrichStatus] = useState<StatusType>('idle')
    const [enrichErrorText, setEnrichErrorText] = useState<string>('')
    const [enrichResult, setEnrichResult] = useState<GuidingCareReplicaEnrichResponse | null>(null)

    const hasAccess = useMemo(
        () => hasAnyRole(Roles.ITSupport),
        [hasAnyRole]
    )

    const tableItems = activityData?.items || []
    const summary = activityData?.summary
    const pagination = activityData?.pagination
    const directPages = useMemo(
        () => buildDirectPageNumbers(pagination?.page || 1, pagination?.totalPages || 1),
        [pagination?.page, pagination?.totalPages]
    )

    const toUtcIsoString = (localDateTime: string): string | undefined => {
        if (!localDateTime) return undefined
        const parsed = new Date(localDateTime)
        if (Number.isNaN(parsed.getTime())) return undefined
        return parsed.toISOString()
    }

    const toUtcIsoDateBoundary = (localDate: string, boundary: 'start' | 'end'): string | undefined => {
        if (!localDate) return undefined

        const parsed = new Date(`${localDate}T00:00:00`)
        if (Number.isNaN(parsed.getTime())) return undefined

        if (boundary === 'end') {
            parsed.setHours(23, 59, 59, 999)
        }

        return parsed.toISOString()
    }

    const isValidDateRange = (fromUtc?: string, toUtc?: string): boolean => {
        if (!fromUtc || !toUtc) {
            setReportErrorText('Enter valid From and To dates before loading the report.')
            return false
        }

        if (new Date(fromUtc).getTime() > new Date(toUtc).getTime()) {
            setReportErrorText('From date must be earlier than or equal to To date.')
            return false
        }

        return true
    }

    const onReset = async () => {
        setResetStatus('saving')
        setResetErrorText('')

        const payload: GuidingCareReplicaResetPollDateRequest = {
            lastPollDateUtc: toUtcIsoString(lastPollDateLocal),
            shiftHours
        }

        try {
            const response = await ApiPost<GuidingCareReplicaResetPollDateResponse>(
                'GuidingCareReplicaAdmin/reset-last-poll-date',
                undefined,
                payload as unknown as Record<string, unknown>
            )

            setResult(response)
            setResetStatus('success')
            await loadReport()
        } catch {
            setResetStatus('error')
            setResetErrorText('Unable to reset last poll date. Verify your role access and API availability.')
        }
    }

    const loadReport = async (options?: ReportLoadOptions) => {
        const selectedSource = options?.source || reportSource
        const selectedPage = options?.page || reportPage
        const selectedPageSize = options?.pageSize || reportPageSize
        const fromUtc = toUtcIsoDateBoundary(reportFromLocal, 'start')
        const toUtc = toUtcIsoDateBoundary(reportToLocal, 'end')

        if (!isValidDateRange(fromUtc, toUtc)) {
            return
        }

        setLoadingReport(true)
        setReportErrorText('')

        try {
            const response = await ApiFetch<GuidingCareReplicaActivityResponse>('GuidingCareReplicaAdmin/activity', {
                fromUtc,
                toUtc,
                source: selectedSource,
                page: selectedPage,
                pageSize: selectedPageSize
            })

            setActivityData(response)
            setReportSource(response.source)
            setReportPage(response.pagination.page)
            setReportPageSize(response.pagination.pageSize)
        } catch {
            setReportErrorText('Unable to load activity report. Verify your role access and API availability.')
        } finally {
            setLoadingReport(false)
        }
    }

    const onSourceChange = async (source: GuidingCareReplicaActivitySource) => {
        setReportSource(source)
        setReportPage(1)
        await loadReport({ source, page: 1 })
    }

    const onPageSizeChange = async (rawValue: string) => {
        const parsed = Number(rawValue)
        const safePageSize = Number.isNaN(parsed) ? reportPageSize : parsed

        setReportPage(1)
        setReportPageSize(safePageSize)
        await loadReport({ page: 1, pageSize: safePageSize })
    }

    const onPageChange = async (page: number) => {
        setReportPage(page)
        await loadReport({ page })
    }

    const onReprocess = async () => {
        setReprocessStatus('saving')
        setReprocessErrorText('')
        try {
            const response = await ApiPost<GuidingCareReplicaReprocessResponse>(
                'GuidingCareReplicaAdmin/reprocess',
                undefined,
                {
                    lookbackHours: reprocessLookbackHours,
                    shiftHours: reprocessShiftHours
                }
            )
            setReprocessResult(response)
            setReprocessStatus('success')
            await loadReport()
        } catch {
            setReprocessStatus('error')
            setReprocessErrorText('Unable to update reprocess watermark.')
        }
    }

    const onEnrich = async () => {
        setEnrichStatus('saving')
        setEnrichErrorText('')
        try {
            const response = await ApiPost<GuidingCareReplicaEnrichResponse>(
                'GuidingCareReplicaAdmin/enrich',
                undefined,
                {
                    processPendingNotifications: true
                }
            )
            setEnrichResult(response)
            setEnrichStatus('success')
            await loadReport()
        } catch {
            setEnrichStatus('error')
            setEnrichErrorText('Unable to run enrichment action.')
        }
    }

    useEffect(() => {
        if (!hasAccess)
            return

        void loadReport()
        // Run on mount for authorized users with the default date range.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasAccess])

    useEffect(() => {
        if (!pagination)
            return

        if (pagination.page !== lastFocusedPageRef.current) {
            lastFocusedPageRef.current = pagination.page
            activityHeadingRef.current?.focus()
        }
    }, [pagination])

    if (!hasAccess) {
        return (
            <>
                <PageHeading pageTitle="Sourcing Admin" createButton={false} filterButton={false} />
                <p>You do not have access to this page.</p>
            </>
        )
    }

    return (
        <>
            <PageHeading pageTitle="Sourcing Admin" createButton={false} filterButton={false} />

            <section className="grid-x grid-margin-x">
                <div className="cell large-12">
                    <div className="callout" style={{ border: '1px solid #dbe4ee', borderRadius: 12, boxShadow: '0 8px 28px rgba(0,0,0,0.06)' }}>
                        <h3 style={{ marginTop: 0, marginBottom: '0.4rem' }}>Reporting</h3>
                        <p style={{ marginTop: 0, color: '#5f6b7a', marginBottom: '1rem' }}>
                            {reportSource === 'Replica'
                                ? 'Replica activity snapshot and unresolved/failed authorization events.'
                                : 'AuthSync activity snapshot and unresolved/failed authorization events.'}
                        </p>

                        <div className="grid-x grid-margin-x">
                            <div className="cell large-3 medium-6 small-12">
                                <label htmlFor="gc-report-from">From (local date)</label>
                                <input
                                    id="gc-report-from"
                                    type="date"
                                    value={reportFromLocal}
                                    onChange={(e) => {
                                        setReportFromLocal(e.target.value)
                                        setReportPage(1)
                                    }}
                                />
                            </div>
                            <div className="cell large-3 medium-6 small-12">
                                <label htmlFor="gc-report-to">To (local date)</label>
                                <input
                                    id="gc-report-to"
                                    type="date"
                                    value={reportToLocal}
                                    onChange={(e) => {
                                        setReportToLocal(e.target.value)
                                        setReportPage(1)
                                    }}
                                />
                            </div>
                            <div className="cell large-2 medium-6 small-12">
                                <label>Source</label>
                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                    <button
                                        type="button"
                                        className={reportSource === 'Replica' ? 'button' : 'hollow button'}
                                        onClick={() => void onSourceChange('Replica')}
                                        disabled={loadingReport}
                                    >
                                        Replica
                                    </button>
                                    <button
                                        type="button"
                                        className={reportSource === 'AuthSync' ? 'button' : 'hollow button'}
                                        onClick={() => void onSourceChange('AuthSync')}
                                        disabled={loadingReport}
                                    >
                                        AuthSync
                                    </button>
                                </div>
                            </div>
                            <div className="cell large-2 medium-6 small-12">
                                <label htmlFor="gc-page-size">Page Size</label>
                                <select
                                    id="gc-page-size"
                                    value={reportPageSize}
                                    onChange={(e) => void onPageSizeChange(e.target.value)}
                                    disabled={loadingReport}
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                            <div className="cell large-2 medium-6 small-12" style={{ alignSelf: 'end' }}>
                                <button type="button" onClick={() => void loadReport()} disabled={loadingReport}>
                                    {loadingReport ? 'Loading...' : 'Refresh Report'}
                                </button>
                            </div>
                        </div>

                        {!!reportErrorText && (
                            <p role="alert" style={{ color: '#b30000', marginTop: '0.75rem' }}>{reportErrorText}</p>
                        )}

                        {activityData && (
                            <div className="grid-x grid-margin-x" style={{ marginTop: '0.75rem', marginBottom: '1rem' }}>
                                <div className="cell large-2 medium-4 small-6">
                                    <div style={{ background: '#ffffff', borderRadius: 10, padding: '0.75rem 0.8rem', borderLeft: '4px solid #0b4a7f', boxShadow: '0 4px 14px rgba(0,0,0,0.05)' }}>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6a7787', textTransform: 'uppercase' }}>Inserted</div>
                                        <div style={{ fontSize: 26, fontWeight: 800, color: '#0b4a7f', lineHeight: 1.1 }}>{summary?.insertedCount || 0}</div>
                                    </div>
                                </div>
                                <div className="cell large-2 medium-4 small-6">
                                    <div style={{ background: '#ffffff', borderRadius: 10, padding: '0.75rem 0.8rem', borderLeft: '4px solid #0b7f65', boxShadow: '0 4px 14px rgba(0,0,0,0.05)' }}>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6a7787', textTransform: 'uppercase' }}>Updated</div>
                                        <div style={{ fontSize: 26, fontWeight: 800, color: '#0b7f65', lineHeight: 1.1 }}>{summary?.updatedCount || 0}</div>
                                    </div>
                                </div>
                                <div className="cell large-2 medium-4 small-6">
                                    <div style={{ background: '#ffffff', borderRadius: 10, padding: '0.75rem 0.8rem', borderLeft: '4px solid #a86600', boxShadow: '0 4px 14px rgba(0,0,0,0.05)' }}>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6a7787', textTransform: 'uppercase' }}>Skipped</div>
                                        <div style={{ fontSize: 26, fontWeight: 800, color: '#a86600', lineHeight: 1.1 }}>{summary?.skippedCount || 0}</div>
                                    </div>
                                </div>
                                <div className="cell large-2 medium-4 small-6">
                                    <div style={{ background: '#ffffff', borderRadius: 10, padding: '0.75rem 0.8rem', borderLeft: '4px solid #9b1c1c', boxShadow: '0 4px 14px rgba(0,0,0,0.05)' }}>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6a7787', textTransform: 'uppercase' }}>Errors</div>
                                        <div style={{ fontSize: 26, fontWeight: 800, color: '#9b1c1c', lineHeight: 1.1 }}>{summary?.errorCount || 0}</div>
                                    </div>
                                </div>
                                <div className="cell large-2 medium-4 small-6">
                                    <div style={{ background: '#ffffff', borderRadius: 10, padding: '0.75rem 0.8rem', borderLeft: '4px solid #1f2937', boxShadow: '0 4px 14px rgba(0,0,0,0.05)' }}>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#6a7787', textTransform: 'uppercase' }}>Runs</div>
                                        <div style={{ fontSize: 26, fontWeight: 800, color: '#1f2937', lineHeight: 1.1 }}>{summary?.runsCount || 0}</div>
                                    </div>
                                </div>
                                <div className="cell large-12" style={{ marginTop: '0.4rem', color: '#4f5b6b', fontSize: 13 }}>
                                    <strong>Last Run:</strong> {displayLocalDateTime(summary?.lastRunUtc)}
                                </div>
                            </div>
                        )}

                        {(loadingReport || activityData) && (
                            <>
                                <h4 ref={activityHeadingRef} tabIndex={-1} style={{ marginTop: '1rem' }}>
                                    Error and Skipped Activity (Showing {tableItems.length} of {pagination?.totalCount || 0})
                                </h4>
                                <div style={{ overflowX: 'auto', border: '1px solid #dbe4ee', borderRadius: 10, backgroundColor: '#fff' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f5f8fb' }}>
                                                <th style={{ padding: '0.75rem', fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#5c6978' }}>Date</th>
                                                <th style={{ padding: '0.75rem', fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#5c6978' }}>Source</th>
                                                <th style={{ padding: '0.75rem', fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#5c6978' }}>Type</th>
                                                <th style={{ padding: '0.75rem', fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#5c6978' }}>Level</th>
                                                <th style={{ padding: '0.75rem', fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#5c6978' }}>Auth ID</th>
                                                <th style={{ padding: '0.75rem', fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#5c6978' }}>
                                                    {reportSource === 'AuthSync' ? 'Status' : 'Resolved Date'}
                                                </th>
                                                <th style={{ padding: '0.75rem', fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#5c6978' }}>Message</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loadingReport && (
                                                <tr>
                                                    <td colSpan={7} style={{ padding: '1.25rem', color: '#5c6978' }}>
                                                        <div className="center" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem' }}>
                                                            <img src="/spinning-circles.svg" alt="loading" style={{ width: '2rem', height: '2rem' }} />
                                                            <span>Loading report...</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                            {!loadingReport && tableItems.length === 0 && (
                                                <tr>
                                                    <td colSpan={7} style={{ padding: '1rem', color: '#5c6978' }}>
                                                        No activity records match the selected source and date range.
                                                    </td>
                                                </tr>
                                            )}
                                            {!loadingReport && tableItems.map((item, idx) => (
                                                <tr key={idx} style={{ borderTop: '1px solid #eef2f6' }}>
                                                    <td style={{ padding: '0.75rem', fontSize: 13, color: '#1f2937' }}>{displayLocalDateTime(item.dateUtc)}</td>
                                                    <td style={{ padding: '0.75rem', fontSize: 12, color: '#334155' }}>{item.sourceTask || 'N/A'}</td>
                                                    <td style={{ padding: '0.75rem', fontSize: 12, color: '#334155' }}>{item.eventType || 'Info'}</td>
                                                    <td style={{ padding: '0.75rem' }}>
                                                        <span style={{ ...getLevelBadgeStyle(item.level), display: 'inline-block', fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '0.2rem 0.55rem' }}>
                                                            {item.level || 'Info'}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '0.75rem', fontSize: 13, fontWeight: 700, color: '#0b4a7f' }}>{item.authorizationId || 'N/A'}</td>
                                                    <td style={{ padding: '0.75rem', fontSize: 13, color: '#1f2937' }}>
                                                        {reportSource === 'AuthSync'
                                                            ? (item.requestStatus || 'Unknown')
                                                            : (item.isResolved
                                                                ? displayLocalDateTime(item.resolvedDateUtc || item.authInsertedOnUtc)
                                                                : 'Unresolved')}
                                                    </td>
                                                    <td style={{ padding: '0.75rem', fontSize: 13, color: '#334155', maxWidth: 440 }}>{item.message || item.error || 'N/A'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {pagination && (
                                    <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                        <div style={{ color: '#4f5b6b', fontSize: 13 }}>
                                            Page {pagination.page} of {pagination.totalPages} ({pagination.totalCount} total records)
                                        </div>
                                        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                                            <button
                                                type="button"
                                                className="hollow button tiny"
                                                aria-label="Go to previous page"
                                                onClick={() => void onPageChange(pagination.page - 1)}
                                                disabled={loadingReport || pagination.page <= 1}
                                            >
                                                Previous
                                            </button>

                                            {directPages.map((pageNumber) => (
                                                <button
                                                    key={pageNumber}
                                                    type="button"
                                                    aria-label={`Go to page ${pageNumber}`}
                                                    aria-current={pageNumber === pagination.page ? 'page' : undefined}
                                                    className={pageNumber === pagination.page ? 'button tiny' : 'hollow button tiny'}
                                                    onClick={() => void onPageChange(pageNumber)}
                                                    disabled={loadingReport}
                                                >
                                                    {pageNumber}
                                                </button>
                                            ))}

                                            <button
                                                type="button"
                                                className="hollow button tiny"
                                                aria-label="Go to next page"
                                                onClick={() => void onPageChange(pagination.page + 1)}
                                                disabled={loadingReport || pagination.page >= pagination.totalPages}
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </section>

            <section className="grid-x grid-margin-x">
                <div className="cell large-7 medium-9 small-12">
                    <div className="callout">
                        <h3 style={{ marginTop: 0 }}>Reset Last Poll Date</h3>
                        <p style={{ marginBottom: '1rem' }}>
                            Use this when the background task was paused/off and you need to move the watermark manually.
                        </p>

                        <label htmlFor="gc-last-poll-date">Last Poll Date (local time, optional)</label>
                        <input
                            id="gc-last-poll-date"
                            type="datetime-local"
                            value={lastPollDateLocal}
                            onChange={(e) => setLastPollDateLocal(e.target.value)}
                        />

                        <label htmlFor="gc-shift-hours">Shift Hours</label>
                        <input
                            id="gc-shift-hours"
                            type="number"
                            value={shiftHours}
                            onChange={(e) => setShiftHours(Number(e.target.value))}
                        />

                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                            <button type="button" className="hollow button" onClick={() => setShiftHours(-52)}>
                                Use -52
                            </button>
                        </div>

                        <button type="button" onClick={onReset} disabled={resetStatus === 'saving'}>
                            {resetStatus === 'saving' ? 'Resetting...' : 'Reset Last Poll Date'}
                        </button>

                        {resetStatus === 'error' && <p style={{ color: '#b30000', marginTop: '0.75rem' }}>{resetErrorText}</p>}

                        {resetStatus === 'success' && result && (
                            <div className="callout success" style={{ marginTop: '1rem' }}>
                                <p><strong>Task:</strong> {result.taskName}</p>
                                <p><strong>Previous:</strong> {displayLocalDateTime(result.previousLastPollDateUtc)}</p>
                                <p><strong>New:</strong> {displayLocalDateTime(result.newLastPollDateUtc)}</p>
                                <p><strong>Shift Hours:</strong> {result.appliedShiftHours}</p>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <section className="grid-x grid-margin-x">
                <div className="cell large-7 medium-9 small-12">
                    <div className="callout">
                        <h3 style={{ marginTop: 0 }}>Enrichment</h3>
                        <p>Processes pending notification groups generated by replica ingestion.</p>

                        <button type="button" onClick={onEnrich} disabled={enrichStatus === 'saving'}>
                            {enrichStatus === 'saving' ? 'Processing...' : 'Run Enrichment'}
                        </button>

                        {enrichStatus === 'error' && <p style={{ color: '#b30000', marginTop: '0.75rem' }}>{enrichErrorText}</p>}
                        {enrichStatus === 'success' && enrichResult && (
                            <div className="callout success" style={{ marginTop: '1rem' }}>
                                <p><strong>Email Groups:</strong> {enrichResult.emailGroupCount}</p>
                                <p><strong>Text Groups:</strong> {enrichResult.textMessageGroupCount}</p>
                                <p><strong>Processed On:</strong> {displayLocalDateTime(enrichResult.processedOnUtc)}</p>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </>
    )
}

export default GuidingCareReplicaAdmin
